import React, { useState, FC, ImgHTMLAttributes } from 'react';
import cn from 'clsx';
import { css } from 'linaria';

import LazyContent from '../LazyContent';

import colors from '../../utils/colorConstants';

// padding-bottom hack is used to force an aspect ratio.
// it's a ratio of image height / width.
const styles = {
  tallContainer: css`
    padding-left: 11%;
    padding-right: 11%;
  `,
  tall: css`
    &::before {
      padding-bottom: 138%;
    }
  `,
  squareContainer: css`
    padding-left: 4.5%;
    padding-right: 4.5%;
  `,
  square: css`
    &::before {
      padding-bottom: 100%;
    }
  `,
  horizontalContainer: css`
    padding-left: 4.5%;
    padding-right: 4.5%;
  `,
  horizontal: css`
    &::before {
      padding-bottom: 72.5%;
    }
  `,
  noveltyContainer: css`
    padding-left: 4.5%;
    padding-right: 4.5%;
  `,
  novelty: css`
    &::before {
      padding-bottom: 110%;
    }
  `,
  fullContainer: css``,
  full: css``,
};

// Animating by using opacity only. This is easier on the browser due to how it processes graphics.
const LoadingAnimation = css`
  &:before {
    content: '';
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    animation: loading-pulse 1s ease-in-out alternate infinite;
    background-color: ${colors.dove};
  }

  .loaded&:after {
    animation: loading-finished 1s ease-out;
    animation-fill-mode: forwards;
  }

  @keyframes loading-pulse {
    0% {
      opacity: 0.9;
    }
    100% {
      opacity: 0.5;
    }
  }

  @keyframes loading-finished {
    0% {
      opacity: 0.7;
    }
    100% {
      opacity: 0;
    }
  }
`;

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  shape: 'tall' | 'square' | 'horizontal' | 'novelty' | 'full';
  cardClassName?: string;
  priority?: boolean;
}

const CardImage: FC<Props> = ({
  alt,
  cardClassName,
  className,
  priority = false,
  shape,
  src,
  ...props
}) => {
  const containerClass = styles[`${shape}Container` as const];
  const [isImageLoaded, setIsLoaded] = useState(priority);

  // Once loaded, AspectRatioStyles can be removed and will make images that are smaller than their predefined size not take up as much space.
  return (
    <div className={`w-full ${containerClass} ${className}`}>
      <LazyContent
        className={cn('flex items-end relative', styles[shape], LoadingAnimation, {
          loaded: isImageLoaded,
        })}
        priority={priority}
      >
        <img
          {...props}
          alt={alt}
          className={`w-full h-full object-contain object-bottom  ${cardClassName}`}
          // @ts-expect-error
          importance={priority ? 'high' : 'auto'}
          onLoad={() => setIsLoaded(true)}
          src={src}
        />
      </LazyContent>
    </div>
  );
};

export default CardImage;

/*
Removed PICTURE code
<LazyPicture 
  className={
    cn(
      "flex items-end relative",
      styles[shape],
      LoadingAnimation,
      { loaded: isImageLoaded }
    )
  }
  imageClassName={`w-full h-full object-contain object-bottom ${cardClassName}`}
  image={src}
  priority={priority}
  onLoad={() => setIsLoaded(true)}
  alt={alt}
/>
*/
